import React, { useCallback, useEffect, useRef, useState } from "react";

import { useCardDraft } from "../common/useCardDraft";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import Select from "components/common/Select";
import { Stepper, YesNo } from "../controls";
import IcdAutocomplete from "../chiefComplaints/IcdAutocomplete";
import {
  durationToSinceWhen,
  formatShortDate,
  sinceWhenToDuration,
  toIsoDateOnly,
  toIsoUtc,
} from "../chiefComplaints/dateHelpers";
import {
  TREATMENT_STATUS,
  ongoingFromStatusKey,
  statusKeyFromOngoing,
  treatmentBand,
} from "../treatmentStatus";
import DiagnosisService from "./DiagnosisService";
import { latestSinceWhen } from "./episodeDates";
import LinkedRefsChipStrip from "./LinkedRefsChipStrip";
import RefPickerModal from "./RefPickerModal";
import LabPickerModal from "./LabPickerModal";
import { fetchMedicines, fetchComplaints, fetchLabs } from "./linkedRefsData";
import { DX_V2_REFRESH } from "./events";
import { CalendarIcon, ChevronUpIcon } from "../labInvestigations/labIcons";

/**
 * ONE diagnosis sheet — add or update — owning all of its own field state.
 *
 * This used to be the body of DiagnosisEditor, with every field hoisted into
 * that component and gated on formOpen + target + a submittingRef. It was split
 * out so the rail can hold a STACK of sheets: each card needs its own
 * diagnosis / ongoing / duration / description / three linked-ref selections,
 * and sibling cards must not see each other's values. The rail
 * (DiagnosisEditor) now only decides which cards exist and in what order;
 * everything below the card border lives here.
 *
 * Two modes, unchanged from before the split:
 *   add    (DX_V2_ADD, from the toolbar's "+ Add diagnosis") — ICD fuzzy
 *          search · Consulted doctor?* beside Ongoing?* · duration steppers +
 *          since-when auto-calc · description (max 250 words) · the three
 *          linked-refs strips (shown after a formal ICD pick) ·
 *          Cancel / Save diagnosis.
 *   update (DX_V2_STATUS, from a row's treatment-status chip) — "Update ·
 *          <name>" head · Treatment status beside Consulted doctor? ·
 *          status-update-date steppers · description · the same three strips,
 *          always shown (the diagnosis is already on file, so there is no ICD
 *          pick to wait for) · Cancel / Update → POST /{diagnosisId}/status
 *          with the dialog's exact body.
 *
 * The dialog asked "Ongoing? Yes/No"; the rail asks for the TREATMENT STATUS
 * itself — the same two options the row chip renders, off the same
 * ../treatmentStatus source, so the picker and the chip cannot drift. It maps
 * to the identical `ongoing` bool on the wire (Ongoing = true), which is what
 * drives the server's reopen-vs-resolve branch (case R when was-resolved and
 * ongoing comes back true, case D when was-ongoing and it comes back false) and
 * therefore the "Resolved on" date the past table reads.
 */

const MAX_DESCRIPTION_WORDS = 250;

const todayISO = () => new Date().toISOString().slice(0, 10);

// Same helper ComplaintForm uses to seed/bound its date pickers from a
// Date / ISO value: fall back to today when the input is missing or
// unparseable so the input element never gets handed a NaN.
const dateInputFrom = (value) => {
  const d = value ? new Date(value) : new Date();
  return (isNaN(d.getTime()) ? new Date() : d).toISOString().slice(0, 10);
};

const countWords = (s) => {
  const trimmed = (s || "").trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).length;
};

const clampWords = (next, max) => {
  if (countWords(next) <= max) return next;
  const parts = next.split(/(\s+)/);
  let words = 0;
  const out = [];
  for (const chunk of parts) {
    if (/^\s+$/.test(chunk)) {
      out.push(chunk);
      continue;
    }
    if (chunk === "") continue;
    if (words >= max) break;
    out.push(chunk);
    words += 1;
  }
  return out.join("").replace(/\s+$/, "");
};

/**
 * Linked refs come back as bare ids on some records and as objects on others
 * (Diagnosis.js's medicines cell already reads both shapes). Normalise to the
 * string ids the strips select on and the API stores — a plain `.map(String)`
 * would seed "[object Object]", and re-saving would then drop the link.
 */
const refIds = (list) =>
  (list || [])
    .map((r) => {
      if (r == null) return "";
      if (typeof r !== "object") return String(r);
      return String(
        r.id || r.medicineId || r.complaintId || r.investigationId || ""
      );
    })
    .filter(Boolean);

// Map AI's treatment_status enum to the "yes"/"no" the Ongoing YesNo
// stores. "ongoing" → "yes"; every other value (completed/scheduled/none)
// → "no". Kept in a helper so add-time seeding + submit-time inversion
// stay in sync.
const ongoingSegFromStatus = (s) => {
  const norm = String(s || "").trim().toLowerCase();
  return norm === "ongoing" ? "yes" : "no";
};

const DiagnosisForm = ({
  formId,
  patientId,
  editing,
  ordinal,
  showOrdinal,
  blockedBySibling,
  onFilledChange,
  onSavingChange,
  onClose,
  // Transcript-mode extensions (all optional; defaults preserve standalone).
  initialPayload,
  onSave,
  onReject,
  extraActiveKeys,
  saving,
  error,
  themeClass,
  menuClassName,
  lockName,
  isReopen,
}) => {
  // `target` was the old editor's name for the record being updated; it is now
  // simply the sheet's `editing` prop, fixed for the sheet's whole lifetime.
  const isStatus = !!editing;
  const cur = (editing && editing.current) || {};
  const p = initialPayload || null;
  const isTranscript = typeof onSave === "function";
  // Name + ICD are chosen at first-time add. Every re-open (update, in-call
  // re-edit, review click-to-edit) locks them. Update mode implies this
  // via `isStatus`; add-mode transcript callers set `lockName` when
  // reopening a saved captured item.
  const lockNameEffective = isStatus || !!lockName;
  // Transcript flow: skip the linked-refs pickers (per SECTIONS_SPEC.md,
  // Diagnosis-first-cut simplification B — AI doesn't have ObjectID
  // references, and cross-service id resolution mid-call is out of scope).
  // Reviewer can link refs via the standalone editor after Save & next.
  const hideLinkedRefs = isTranscript;

  /**
   * Seeded in the useState INITIALISERS, not an effect: a sheet mounts once per
   * record, so the record it opens on can never change under it. This is the
   * old openStatus() seed rule verbatim — every field an exact snapshot of
   * what's on the record, with `editing.<refs>` fallbacks covering the flat
   * shape the table renders from, so opening an update can never silently
   * blank a link and then save it away.
   */
  // Seed with concrete numbers when the record / transcript provides a
  // sinceWhen; otherwise leave the steppers empty (null) so they render
  // "-" and the "From today" toggle can be used to log a same-day entry
  // without typing 0/0/0. Add-mode without an AI seed lands here.
  //
  // ZY-10312 — the Status update card (isStatus) ALWAYS defaults to
  // "today" on open now (mirrors Medicine wave-1 Req 5 + the CC Update
  // card). Steppers seed to 0/0/0 so they read "0" (not "-") alongside
  // the pre-ON "From today" toggle; the auto-calc Status update date
  // reads today immediately. Reviewer can type any duration to shift
  // back inside the audit's allowed window. Previously
  // `sinceWhenToDuration(cur.sinceWhen)` seeded the previous status
  // entry's own age, which read as "already set, nothing to do" even
  // when the reviewer intended a fresh entry.
  const seedDuration = isStatus
    ? { years: 0, months: 0, days: 0 }
    : (p && p.since_date) ? sinceWhenToDuration(p.since_date) : null;
  // "From today" toggle — seeds ON for a Status update (per above) or
  // when the Add source date parses to a zero duration (record's
  // sinceWhen or AI seed IS today), so the auto-calc line lands on
  // today without a mandatory reviewer flip.
  const seedFromToday = isStatus || !!(seedDuration
    && seedDuration.years === 0
    && seedDuration.months === 0
    && seedDuration.days === 0);

  // ZY-10356 Phase 1 — transcript-mode draft preservation. Standalone
  // mounts return [null, no-op]; the rest of this block is inert
  // outside the modal.
  const [draft, saveDraft] = useCardDraft(formId);

  const [collapsed, setCollapsed] = useState(false);
  const [diagnosis, setDiagnosis] = useState(() => {
    if (draft && "diagnosis" in draft) return draft.diagnosis;
    if (isStatus) return editing.diagnosis || "";
    if (p) return p.diagnosis || p.name || p.value || "";
    return "";
  });
  const [diagnosisCode, setDiagnosisCode] = useState(() => {
    if (draft && "diagnosisCode" in draft) return draft.diagnosisCode;
    if (isStatus) return editing.diagnosisCode || null;
    if (p && p.icd_code) return { icdCode: p.icd_code, label: p.icd_description || "" };
    return null;
  });
  // AI-suggested ICD chip payload — frozen at mount from the captured
  // item (transcript-mode only). Passed straight to IcdAutocomplete
  // which owns the pill markup + click behavior; DiagnosisForm just
  // carries the shape through. Null on standalone add / update sheets.
  // Mirrors ComplaintForm's rule.
  const aiSuggestion = p && p.icd_code
    ? { icdCode: p.icd_code, extractedText: p.diagnosis || p.name || p.value || "" }
    : null;
  // Both segments are REQUIRED (*) on the board — no silent defaults on
  // add. Transcript-mode consulted seeding falls back to the AI
  // payload's boolean when present (matches ComplaintForm's rule).
  const [consulted, setConsulted] = useState(() => {
    if (draft && "consulted" in draft) return draft.consulted;
    if (isStatus && cur.consultedDoctor !== undefined && cur.consultedDoctor !== null) {
      return cur.consultedDoctor ? "yes" : "no";
    }
    if (p && typeof p.consulted_doctor === "boolean") {
      return p.consulted_doctor ? "yes" : "no";
    }
    return isStatus ? "no" : null;
  });
  const [ongoing, setOngoing] = useState(() => {
    if (draft && "ongoing" in draft) return draft.ongoing;
    if (isStatus) return editing.ongoing === false ? "no" : "yes";
    if (p && p.treatment_status) return ongoingSegFromStatus(p.treatment_status);
    return null;
  });
  // Historical-resolve-on-add: when the reviewer picks `Ongoing = No` on a
  // new diagnosis, this input surfaces so the ongoing-at-onset segment and
  // the resolved row land in the audit together (otherwise the diagnosis
  // would read as "created already resolved" with no prior ongoing row).
  // Clears the moment ongoing flips back to Yes — see changeOngoing below.
  // No `previous status` companion: Dx has no VAS scale, so the
  // pre-resolution state is always `ongoing=true` by construction.
  // Reedit of a completed-on-add card: seed from `resolved_date`
  // (written by bodyToCapturedAdd when ongoing=false on first save).
  // Fresh AI emits / manual adds leave the key unset → default empty
  // preserved. Standalone-add never passes `initialPayload`, unchanged.
  const [resolvedDate, setResolvedDate] = useState(() => {
    if (draft && "resolvedDate" in draft) return draft.resolvedDate;
    if (p && p.resolved_date) return p.resolved_date;
    return "";
  });
  const [years, setYears] = useState(() => (
    draft && "years" in draft ? draft.years : (seedDuration ? seedDuration.years : null)
  ));
  const [months, setMonths] = useState(() => (
    draft && "months" in draft ? draft.months : (seedDuration ? seedDuration.months : null)
  ));
  const [days, setDays] = useState(() => (
    draft && "days" in draft ? draft.days : (seedDuration ? seedDuration.days : null)
  ));
  const [fromToday, setFromToday] = useState(() => (
    draft && "fromToday" in draft ? draft.fromToday : seedFromToday
  ));
  const [description, setDescription] = useState(() => {
    if (draft && "description" in draft) return draft.description;
    if (isStatus) return cur.description || "";
    if (p) return p.description || "";
    return "";
  });
  const [complaintIds, setComplaintIds] = useState(() => (
    draft && "complaintIds" in draft
      ? draft.complaintIds
      : (isStatus ? refIds(cur.complaintIds || editing.complaintIds) : [])
  ));
  const [medicineRefs, setMedicineRefs] = useState(() => (
    draft && "medicineRefs" in draft
      ? draft.medicineRefs
      : (isStatus ? refIds(cur.medicineRefs || editing.medicineRefs) : [])
  ));
  const [labRefs, setLabRefs] = useState(() => (
    draft && "labRefs" in draft
      ? draft.labRefs
      : (isStatus ? refIds(cur.labRefs || editing.labRefs) : [])
  ));
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  // ZY-10356 Phase 1 — sync persistent fields to the modal-root draft
  // store. Ephemeral state (submitting, feedback, picker, local
  // in-flight loaders) is deliberately excluded.
  useEffect(() => {
    saveDraft({
      diagnosis, diagnosisCode, consulted, ongoing,
      resolvedDate, years, months, days, fromToday,
      description, complaintIds, medicineRefs, labRefs,
    });
  }, [
    saveDraft,
    diagnosis, diagnosisCode, consulted, ongoing,
    resolvedDate, years, months, days, fromToday,
    description, complaintIds, medicineRefs, labRefs,
  ]);
  const [picker, setPicker] = useState(null); // null | "medicine" | "complaint" | "lab"

  const [medicines, setMedicines] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [labs, setLabs] = useState([]);

  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  // Set of already-recorded active-diagnosis identifiers (ICD codes first,
  // lowercased name as fallback for free-text-added rows). Fed to the
  // IcdAutocomplete so already-added rows render greyed with an "already
  // added" pill. Only fetched in Add mode — the picker is hidden on the
  // status-update sheet (`lockNameEffective`) so the network call is dead
  // weight there.
  const [activeDiagnosisKeys, setActiveDiagnosisKeys] = useState(() => new Set());
  useEffect(() => {
    if (lockNameEffective || !patientId) return;
    const svc = new DiagnosisService();
    svc.list(patientId, (res) => {
      if (!mounted.current) return;
      const keys = new Set();
      for (const d of (res && res.active) || []) {
        const code = ((d && d.diagnosisCode && d.diagnosisCode.icdCode) || "").trim();
        if (code) keys.add(code.toLowerCase());
        const nm = ((d && d.diagnosis) || "").trim().toLowerCase();
        if (nm) keys.add(nm);
      }
      setActiveDiagnosisKeys(keys);
    });
  }, [patientId, lockNameEffective]);
  const isDiagnosisAlreadyActive = useCallback(
    (row) => {
      const code = ((row && (row.icdCode || row.code)) || "").trim().toLowerCase();
      if (code && activeDiagnosisKeys.has(code)) return true;
      // ZY-10312 — transcript mode passes in-call captures via
      // extraActiveKeys so the dropdown flags items staged *this call*
      // the same way DB-persisted actives are flagged. Keys lowercased
      // upstream in CapturedSections.
      if (code && extraActiveKeys && extraActiveKeys.has(code)) return true;
      const label = ((row && (row.label || row.value)) || "").trim().toLowerCase();
      // Fall back to name match for free-text-added rows whose ICD code
      // never got captured. Trim the leading ICD-code prefix so the name
      // check matches the resolver in IcdAutocomplete's render.
      const stripped = code && label.startsWith(code)
        ? label.slice(code.length).replace(/^[\s.:\-–—]+/, "")
        : label;
      if (!stripped) return false;
      return activeDiagnosisKeys.has(stripped) || (extraActiveKeys && extraActiveKeys.has(stripped));
    },
    [activeDiagnosisKeys, extraActiveKeys],
  );

  // The rail needs to know whether this sheet has a diagnosis on it yet: a
  // second "+ Add diagnosis" click must not bury an untouched blank sheet under
  // a new one. Update sheets always count as filled — their diagnosis is fixed
  // and comes from the record. Only invoked when the caller wired the callback
  // (standalone SheetStack does; transcript doesn't).
  useEffect(() => {
    if (typeof onFilledChange === "function") {
      onFilledChange(formId, isStatus || !!diagnosis.trim());
    }
  }, [formId, isStatus, diagnosis, onFilledChange]);

  // Linked-refs strips appear only after a FORMAL ICD pick when adding; an
  // update always shows them (its diagnosis is already on file). Transcript
  // mode hides them entirely (see hideLinkedRefs). Lazy-load then.
  const hasDiagnosisPick = !!(diagnosisCode && (diagnosisCode.icdCode || diagnosisCode.code));
  const showStrips = !hideLinkedRefs && (isStatus || hasDiagnosisPick);
  const icdKey = diagnosisCode && (diagnosisCode.icdCode || diagnosisCode.code);
  useEffect(() => {
    if (!showStrips || !patientId) return;
    fetchMedicines(patientId).then((r) => mounted.current && setMedicines(r));
    fetchComplaints(patientId).then((r) => mounted.current && setComplaints(r));
    fetchLabs(patientId).then((r) => mounted.current && setLabs(r));
  }, [showStrips, icdKey, patientId]);

  // When the reviewer manually lands the trio on 0/0/0 (typing zeros or
  // arrowing every field down), auto-flip "From today" on. All-zero is
  // semantically "today" — the toggle IS the way we record that intent,
  // so leaving it off would let a same-day entry save with a null derived
  // date. Only fires when the values are explicit zeros; leaves nulls
  // ("-" state) alone so the reviewer can still opt out.
  useEffect(() => {
    if (years === 0 && months === 0 && days === 0 && !fromToday) {
      setFromToday(true);
    }
  }, [years, months, days, fromToday]);
  const durationHasAny = years > 0 || months > 0 || days > 0;
  const derivedSinceWhen = fromToday
    ? new Date()
    : durationHasAny
    ? durationToSinceWhen(years, months, days)
    : null;
  // Save is allowed when the reviewer either typed a real duration OR
  // opted into "From today". Empty duration with the toggle off saves
  // nothing (auto-calc reads "—") — no more accidental "since today".
  const durationPicked = fromToday || durationHasAny;
  const isBusy = submitting || !!saving;

  // Historical-resolve ONLY applies to the Add path (Dx v2's UpdateStatus
  // endpoint has no equivalent — resolving an existing diagnosis uses the
  // normal single-entry status write).
  const needsHistoricalResolve = !isStatus && ongoing === "no";
  const resolvedDateMinISO = derivedSinceWhen ? dateInputFrom(derivedSinceWhen) : "";
  const historicalResolveValid = !needsHistoricalResolve || (
    !!resolvedDate
    && (!resolvedDateMinISO || resolvedDate >= resolvedDateMinISO)
    && resolvedDate <= todayISO()
  );
  // Reset the resolved-date input the moment ongoing leaves `no` — a stale
  // value would otherwise ride into a subsequent save if the reviewer
  // flipped back and had already filled it.
  const changeOngoing = (next) => {
    setOngoing(next);
    if (next !== "no") {
      setResolvedDate("");
    }
  };

  // Status-update mode: the derived date must land inside the current
  // episode window [episode start, today]. A date-input's grey-out isn't
  // available here (steppers, not a picker), so we block Save + show an
  // inline hint instead. Add mode is unconstrained — a fresh diagnosis
  // has no prior episode to bracket against.
  // Lower bound for the status update's derived sinceWhen: the latest
  // audit entry's sinceWhen (NOT the episode start). A new status row
  // must be dated on or after the most recent entry already logged so
  // the clinician can't record a change that predates one already saved.
  const episodeStartMs = isStatus
    ? new Date(
        latestSinceWhen(
          editing && editing.statusHistory,
          cur.sinceWhen || (editing && editing.addedAt),
        ),
      ).getTime()
    : null;
  const derivedMs = derivedSinceWhen ? new Date(derivedSinceWhen).getTime() : null;
  const outOfEpisode =
    isStatus &&
    derivedMs != null &&
    episodeStartMs != null &&
    !Number.isNaN(episodeStartMs) &&
    derivedMs < episodeStartMs;
  // Free-text diagnoses are not allowed on Add: Save is gated on a
  // real ICD pick (hasDiagnosisPick — computed above). Mirrors the
  // Chief-complaints ComplaintForm gate: the IcdAutocomplete onChange
  // contract nulls diagnosisCode the moment the reviewer types over
  // the picked text, so this reliably re-blocks Save until they pick
  // again from the dropdown. Status (update) path is unaffected —
  // the diagnosis identity is locked during Status writes.
  const canSubmit =
    !blockedBySibling &&
    (isStatus
      ? !isBusy && durationPicked && !outOfEpisode
      : !!diagnosis.trim() &&
        hasDiagnosisPick &&
        !isBusy &&
        consulted !== null &&
        ongoing !== null &&
        durationPicked &&
        historicalResolveValid);

  // Stepper setters drop the "From today" flag the moment a non-zero
  // value lands; toggle handler resets steppers back to null on OFF so
  // the auto-calc line clears with the switch.
  const changeYears = (v) => {
    setYears(v);
    if (v > 0) setFromToday(false);
  };
  const changeMonths = (v) => {
    setMonths(v);
    if (v > 0) setFromToday(false);
  };
  const changeDays = (v) => {
    setDays(v);
    if (v > 0) setFromToday(false);
  };
  const toggleFromToday = () => {
    if (fromToday) {
      setFromToday(false);
      setYears(null);
      setMonths(null);
      setDays(null);
      return;
    }
    // Turning on always resets to 0/0/0 — the toggle IS "since today", so
    // any manually-typed duration is intentionally overridden. Toggle
    // stays interactable regardless of current field values (user request:
    // never grey out).
    setFromToday(true);
    setYears(0);
    setMonths(0);
    setDays(0);
  };

  // The chip's own two options / colours, mapped to the `ongoing` bool the
  // wire carries. Only read while updating — `ongoing` is null until picked
  // on the add form.
  const statusKey = statusKeyFromOngoing(ongoing !== "no");
  const statusBand = treatmentBand(statusKey);

  const toggleId = (setter) => (id) =>
    setter((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const submit = () => {
    if (!canSubmit) return;
    if (!isTranscript && !patientId) return;

    // Body shape is Dx v2's request body (camelCase) for BOTH paths —
    // transcript caller gets the same shape as the standalone writer,
    // then adapts to the captured-item snake_case at its own callsite.
    const body = isStatus
      ? {
          ongoing: ongoing === "yes",
          // Duration / From-today never collect a time of day — strip the
          // wall-clock time the derivation picked up from `new Date()` so
          // same-day multi-write timelines (chart's chronological sort)
          // chain correctly. Switch back to `toIsoUtc` when future UX
          // adds an explicit datetime picker.
          sinceWhen: toIsoDateOnly(derivedSinceWhen),
          description: description.trim(),
          consultedDoctor: consulted === "yes",
          complaintIds,
          medicineRefs,
          labRefs,
        }
      : {
          diagnosis: diagnosis.trim(),
          diagnosisCode: diagnosisCode || undefined,
          ongoing: ongoing === "yes",
          // See the matching comment on the isStatus branch above.
          sinceWhen: toIsoDateOnly(derivedSinceWhen),
          description: description.trim(),
          consultedDoctor: consulted === "yes",
          complaintIds,
          medicineRefs,
          labRefs,
          // Historical-resolve: field only lands on the wire when the
          // reviewer picked `Ongoing = No` AND filled the resolved date.
          // canSubmit already enforces this; guard here is belt-and-braces
          // so a stale non-"no" ongoing doesn't send a stale resolvedDate.
          ...(needsHistoricalResolve && resolvedDate ? {
            resolvedDate: toIsoUtc(resolvedDate),
          } : {}),
        };

    // Transcript mode — hand the body out, caller owns the persistence.
    // No local submitting flag (caller drives `saving`), no DX_V2_REFRESH
    // emit (transcript polls its own state).
    if (isTranscript) {
      onSave(body);
      return;
    }

    setSubmitting(true);
    if (typeof onSavingChange === "function") {
      onSavingChange(formId, true);
    }
    setFeedback(null);
    const svc = new DiagnosisService();
    const done = () => {
      EventService.emit(DX_V2_REFRESH);
      // Unmounted means the rail already dropped this sheet, and closeSheet
      // clears the saving flag on the way out — so there is nothing to hand
      // back and no parent state to touch.
      if (!mounted.current) return;
      if (typeof onSavingChange === "function") {
        onSavingChange(formId, false);
      }
      // The rail drops this card, which unmounts us — so nothing is reset or
      // re-rendered here. Anything set after this point would be a setState on
      // an unmounted component.
      onClose(formId);
    };
    const failed = (err) => {
      // A version clash means the table moved under us — reload it, which is
      // what Diagnosis.js did while it owned this call. Emitted before the
      // mount check on purpose: the table needs the reload whether or not this
      // sheet is still on screen.
      if (err && err.reason === "version") EventService.emit(DX_V2_REFRESH);
      if (!mounted.current) return;
      if (typeof onSavingChange === "function") {
        onSavingChange(formId, false);
      }
      setSubmitting(false);
      setFeedback({
        kind: "err",
        text:
          (err && err.message) ||
          (isStatus ? "Update failed — please retry" : "Save failed — please retry"),
      });
    };

    // Fresh version per mutation, never cached. Sibling sheets are blocked
    // from saving while this is in flight, so the version read here cannot
    // go stale between the read and the write. Same `body` we hand out on
    // the transcript path (constructed above) — dispatch on `isStatus`.
    svc.list(
      patientId,
      (fresh) => {
        const withVersion = { ...body, expectedVersion: (fresh && fresh.version) || 0 };
        if (isStatus) {
          svc.updateStatus(patientId, editing.diagnosisId, withVersion, done, failed);
        } else {
          svc.add(patientId, withVersion, done, failed);
        }
      },
      failed
    );
  };

  const suggestionLabel = diagnosis.trim() ? `Suggested for ${diagnosis.trim()}` : null;

  // Unique per sheet — stacked cards would otherwise share DOM ids and every
  // label would point at the first card's field.
  const descId = `dg3-desc-${formId}`;
  const resolvedId = `dg3-resolved-date-${formId}`;
  const foldId = `dg3-fold-${formId}`;

  const consultedField = (
    <div className="cc3-field">
      <label className="cc3-lbl">
        Consulted doctor?{" "}<span className="cc3-req">*</span>
      </label>
      {/* `name` is the radiogroup's ARIA label, not a DOM id (YesNo renders
          buttons, not inputs) — duplicates across sheets break nothing, and the
          sheet's own role="group" label disambiguates the stack. Left exactly
          as it was so the accessible name does not change. */}
      <YesNo value={consulted} onChange={setConsulted} name="Consulted doctor?" />
    </div>
  );

  const rootClass = [
    "cc3-form",
    "dg3-form",
    collapsed ? "is-collapsed" : "",
    themeClass || "",
  ].filter(Boolean).join(" ");

  const displayError = feedback && feedback.kind === "err"
    ? feedback.text
    : error || "";
  const displayErrorKind = displayError ? "err" : "";

  return (
    <>
      <div className={rootClass}>
        {/* ZY-10356 Phase 6 Rule 1 — top-right X close (design doc §3)
            only on RE-OPENS (#2/#4/#6/#8). First-opens (#1/#3/#5/#7)
            leave the footer Reject as the sole dismiss affordance. The
            matching gate on CC/Med forms set the convention; this was
            previously ungated on isReopen and showed on every card. */}
        {isTranscript && isReopen ? (
          <button
            type="button"
            className="zy-card-x"
            onClick={() => onClose(formId)}
            aria-label="Close"
          >
            &times;
          </button>
        ) : null}
        {/* The chevron-up is this design system's card-collapse affordance —
            it folds everything below this row, footer included. */}
        {isStatus ? (
          <div className="cc3-lbl-row cc3-update-head">
            <span className="cc3-update-title">
              {/* Inside the title span, not the space-between row — a badge
                  placed as a row child gets flung to the far edge. */}
              {showOrdinal && <span className="zy-ord" aria-hidden="true">{ordinal}</span>}
              Update <span className="cc3-update-name">&middot; {editing.diagnosis}</span>
            </span>
            <button
              type="button"
              className="cc3-lbl-chev"
              onClick={() => setCollapsed((c) => !c)}
              aria-expanded={!collapsed}
              aria-controls={foldId}
              aria-label={collapsed ? "Expand this update" : "Collapse this update"}
            >
              <ChevronUpIcon />
            </button>
          </div>
        ) : lockNameEffective ? (
          <div className="cc3-lbl-row cc3-update-head">
            <span className="cc3-update-title">
              {showOrdinal && <span className="zy-ord" aria-hidden="true">{ordinal}</span>}
              Diagnosis <span className="cc3-update-name">&middot; {diagnosis || "(none)"}</span>
            </span>
            <button
              type="button"
              className="cc3-lbl-chev"
              onClick={() => setCollapsed((c) => !c)}
              aria-expanded={!collapsed}
              aria-controls={foldId}
              aria-label={collapsed ? "Expand this diagnosis" : "Collapse this diagnosis"}
            >
              <ChevronUpIcon />
            </button>
          </div>
        ) : (
          <div className="cc3-lbl-row">
            <label className="cc3-lbl">
              {showOrdinal && <span className="zy-ord" aria-hidden="true">{ordinal}</span>}
              Diagnosis<span className="cc3-req">*</span>{" "}
              <span className="cc3-lbl-hint">&middot; ICD-enabled &middot; fuzzy search</span>
            </label>
            <button
              type="button"
              className="cc3-lbl-chev"
              onClick={() => setCollapsed((c) => !c)}
              aria-expanded={!collapsed}
              aria-controls={foldId}
              aria-label={collapsed ? "Expand this diagnosis" : "Collapse this diagnosis"}
            >
              <ChevronUpIcon />
            </button>
          </div>
        )}

        <div className={`cc3-fold${isStatus ? "" : " is-tight"}`} id={foldId}>
          <div className="cc3-fold-inner">
        {!lockNameEffective && (
          <div className="cc3-field">
            <IcdAutocomplete
              value={diagnosis}
              code={diagnosisCode && diagnosisCode.icdCode}
              onChange={({ complaint: d, complaintCode: cc }) => {
                setDiagnosis(d);
                setDiagnosisCode(cc);
              }}
              disabled={isBusy}
              isAlreadyActive={isDiagnosisAlreadyActive}
              alreadyActiveTitle="Already in the diagnosis list — update it from the row menu"
              placeholder="Search a diagnosis"
              aiSuggestion={aiSuggestion}
            />
          </div>
        )}

        {/* Consulted doctor?* beside Ongoing?* (2961:4762: two 140s, 20 gap);
            updating swaps the ICD-fixed Ongoing segment for the treatment
            status itself. */}
        <div className="cc3-two dg3-two">
          {isStatus ? (
            <>
              <div className="cc3-field">
                <label className="cc3-lbl">
                  Treatment status<span className="cc3-req">*</span>
                </label>
                <div className="dg3-status" style={{ "--cc3-dot": statusBand.dot }}>
                  <Select
                    value={statusKey}
                    options={TREATMENT_STATUS}
                    getKey={(t) => t.key}
                    getLabel={(t) => t.label}
                    disabled={isBusy}
                    onChange={(key) => setOngoing(ongoingFromStatusKey(key) ? "yes" : "no")}
                    /* Transcript modal overlays z-index 1500; the portaled
                       Select menu defaults to 200 (sections.scss:2496).
                       Caller passes menuClassName="ait-menu" so a scoped
                       body-selector bumps it above the overlay. */
                    menuClassName={menuClassName || ""}
                  />
                </div>
              </div>
              {consultedField}
            </>
          ) : (
            <>
              {consultedField}
              <div className="cc3-field">
                <label className="cc3-lbl">
                  Ongoing<span className="cc3-req">*</span>
                </label>
                <YesNo value={ongoing} onChange={changeOngoing} name="Ongoing?" />
              </div>
            </>
          )}
        </div>

        <div className="cc3-field">
          {/* Label + "From today" toggle share the same row so the toggle
              reads as an explicit shortcut for the Y/M/D stack below.
              Checkbox is disabled whenever any stepper carries a non-zero
              number — a real duration and "today" are mutually exclusive.
              Same layout is used for isStatus (Status update date) since
              the underlying wire field (sinceWhen) is the same. */}
          <div className="cc3-duration-head">
            <label className="cc3-lbl">
              {isStatus ? "Status update date" : "Duration"}
              <span className="cc3-req">*</span>
            </label>
            <label className="med3-toggle-wrap cc3-from-today">
              <input
                type="checkbox"
                checked={fromToday}
                onChange={toggleFromToday}
                disabled={isBusy}
              />
              <span className="med3-toggle" aria-hidden="true" />
              <span className="med3-toggle-lbl">From today</span>
            </label>
          </div>
          <div className="cc-duration" role="group" aria-label="Duration">
            <Stepper label="Years" value={years} onChange={changeYears} max={120} nullable disabled={isBusy} />
            <Stepper label="Months" value={months} onChange={changeMonths} max={11} nullable disabled={isBusy} />
            <Stepper label="Days" value={days} onChange={changeDays} max={31} nullable disabled={isBusy} />
          </div>
          <p className="cc3-autocalc">
            Auto-calc &middot; {isStatus ? "Status update date" : "Since when"}:{" "}
            <strong>{formatShortDate(derivedSinceWhen)}</strong>
          </p>
          {outOfEpisode && (
            <p className="fam-field-error">
              Status update date must be on or after the latest audit entry
              ({formatShortDate(new Date(episodeStartMs))}).
            </p>
          )}
        </div>

        {/* Historical-resolve-on-add: when the reviewer picks `Ongoing = No`
            on a new diagnosis we need the date the diagnosis actually
            resolved so the audit carries both the ongoing-at-onset segment
            and the resolve itself in one write. Bounded by the derived
            since-when at the low end and today at the high end (same-day
            resolution is allowed). Hidden on the Update sheet (that path
            uses the single-entry status write). */}
        {needsHistoricalResolve && (
          <div className="cc3-field">
            <label className="cc3-lbl" htmlFor={resolvedId}>
              Resolved date<span className="cc3-req">*</span>
            </label>
            <div className="an3-date-wrap">
              <input
                id={resolvedId}
                className="sec-input"
                type="date"
                value={resolvedDate}
                onChange={(e) => setResolvedDate(e.target.value)}
                min={resolvedDateMinISO || undefined}
                max={todayISO()}
                // Locked until Duration gives us an onset date — otherwise
                // the reviewer could pick an unbounded resolved date and
                // the min= constraint would be meaningless.
                disabled={isBusy || !derivedSinceWhen}
              />
              <span className="an3-date-icon" aria-hidden="true"><CalendarIcon /></span>
            </div>
          </div>
        )}

        <div className="cc3-field">
          <div className="cc3-lbl-row">
            <label className="cc3-lbl" htmlFor={descId}>
              Description <span className="cc3-lbl-opt">optional</span>
            </label>
            <span className="cc3-lbl-max">
              {description
                ? `${countWords(description)} / ${MAX_DESCRIPTION_WORDS} words`
                : `Max ${MAX_DESCRIPTION_WORDS} words`}
            </span>
          </div>
          <textarea
            id={descId}
            className="cc3-textarea"
            rows={4}
            value={description}
            onChange={(e) => setDescription(clampWords(e.target.value, MAX_DESCRIPTION_WORDS))}
            placeholder={isStatus ? "Add a short note about this update…" : ""}
            disabled={isBusy}
          />
        </div>

        {showStrips && (
          <>
            <LinkedRefsChipStrip
              title="Add medicine"
              suggestionLabel={suggestionLabel}
              emptyMessage="No medicines recorded yet · add them first"
              items={medicines}
              selectedIds={medicineRefs}
              getKey={(m) => m.id}
              getLabel={(m) => m.label}
              getStatusMark={(m) => m.statusMark}
              onToggle={toggleId(setMedicineRefs)}
              onOpenPicker={() => setPicker("medicine")}
            />

            <LinkedRefsChipStrip
              title="Add chief complaints"
              suggestionLabel={suggestionLabel}
              emptyMessage="No chief complaints recorded yet · add them first"
              items={complaints}
              selectedIds={complaintIds}
              getKey={(c) => c.id}
              getLabel={(c) => c.label}
              getStatusMark={(c) => c.statusMark}
              onToggle={toggleId(setComplaintIds)}
              onOpenPicker={() => setPicker("complaint")}
            />

            <LinkedRefsChipStrip
              title="Add lab values"
              suggestionLabel={suggestionLabel}
              emptyMessage="No lab values recorded yet · add them first"
              items={labs}
              selectedIds={labRefs}
              getKey={(l) => l.id}
              getLabel={(l) => l.label}
              getStatusMark={(l) => l.statusMark}
              onToggle={toggleId(setLabRefs)}
              onOpenPicker={() => setPicker("lab")}
            />
          </>
        )}

        {displayError && <div className={`cc-feedback is-${displayErrorKind}`}>{displayError}</div>}

        <div className="cc3-foot">
          {/* Stays live while saving ON PURPOSE. A write that never settles
              (the token-refresh path can swallow its callbacks) would otherwise
              pin the stack-wide save lock with no way out — every sheet's Save
              dead and this sheet undismissable until a reload. Closing mid-write
              is safe: the rail clears the lock, and both done() and failed()
              bail on !mounted, so a late response touches nothing. */}
          <button
            type="button"
            className="cc3-cancel"
            onClick={() => {
              if (isTranscript && !isBusy && typeof onReject === "function") {
                onReject();
                return;
              }
              onClose(formId);
            }}
          >
            {isBusy ? "Close" : (isTranscript ? "Reject" : "Cancel")}
          </button>
          <button
            type="button"
            className={`cc3-save${themeClass ? ` ${themeClass}` : ""}`}
            onClick={submit}
            disabled={!canSubmit}
            title={blockedBySibling ? "Another diagnosis is saving" : undefined}
          >
            {isBusy
              ? isStatus
                ? "Updating…"
                : (isTranscript ? "Adding…" : "Saving…")
              : blockedBySibling
              ? "Waiting…"
              : isStatus
              ? "Update"
              : isTranscript
              ? "Add"
              : "Save diagnosis"}
          </button>
        </div>
          </div>
        </div>
      </div>

      {/* The pickers are fixed-position overlays (.cc-modal-backdrop), so
          living inside the sheet rather than beside it changes nothing on
          screen — and each sheet now owns its own picker state. */}
      {picker === "medicine" && (
        <RefPickerModal
          title="Add medicine"
          subtitle={suggestionLabel}
          items={medicines}
          initialSelectedIds={medicineRefs}
          getKey={(m) => m.id}
          getLabel={(m) => m.label}
          getSubLabel={(m) => m.subLabel}
          getStatusMark={(m) => m.statusMark}
          onCommit={setMedicineRefs}
          onClose={() => setPicker(null)}
        />
      )}
      {picker === "complaint" && (
        <RefPickerModal
          title="Add chief complaints"
          subtitle={suggestionLabel}
          items={complaints}
          initialSelectedIds={complaintIds}
          getKey={(c) => c.id}
          getLabel={(c) => c.label}
          getSubLabel={(c) => c.subLabel}
          getStatusMark={(c) => c.statusMark}
          onCommit={setComplaintIds}
          onClose={() => setPicker(null)}
        />
      )}
      {/* The lab strip's picker is the rich per-panel table the dialogs used
          (ranges, flags, latest-per-test) — the plain list the rail briefly
          shipped dropped that, and the update surface must not lose it. */}
      {picker === "lab" && (
        <LabPickerModal
          patientId={patientId}
          initialSelectedIds={labRefs}
          onCommit={setLabRefs}
          onClose={() => setPicker(null)}
        />
      )}
    </>
  );
};

DiagnosisForm.propTypes = {
  /** Stable per-sheet id; namespaces DOM ids and keys the rail's bookkeeping. */
  formId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  /** Standalone (Dx v2 write path) only — the target patient. */
  patientId: PropTypes.number,
  /** null = blank add; a diagnosis record = updating that record's status. */
  editing: PropTypes.object,
  /** 1-based position in the stack, top first. */
  ordinal: PropTypes.number,
  showOrdinal: PropTypes.bool,
  /** A sibling sheet is mid-save; ours must wait so the version read is safe. */
  blockedBySibling: PropTypes.bool,
  onFilledChange: PropTypes.func,
  onSavingChange: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  // Transcript-mode extensions — all optional; unset preserves standalone.
  /**
   * AI-captured payload used to pre-fill the form (add mode only). Same
   * shape the agent emits for diagnosis captures: {diagnosis, icd_code,
   * icd_description, treatment_status, since_when, since_date,
   * description, consulted_doctor}. Fields missing from the payload
   * fall through to the standalone defaults.
   */
  initialPayload: PropTypes.object,
  /**
   * When provided, the form skips its own Dx v2 write path and hands the
   * Dx v2 request body to this callback. Caller owns persistence — used
   * by the transcript flow to stash captured items on the transcript
   * instead of writing directly to Dx v2. Automatically hides the
   * linked-refs strips (per SECTIONS_SPEC.md, Diagnosis-first-cut
   * simplification B).
   */
  onSave: PropTypes.func,
  /** Transcript-mode Reject handler — if set, overrides onClose for the ghost button. */
  onReject: PropTypes.func,
  /** ZY-10312 — transcript-mode set of lowercased ICD codes (and/or
   *  diagnosis names) that have already been staged THIS call. Merged
   *  into the "already added" predicate so the search dropdown flags
   *  in-call duplicates the same way DB-persisted actives are flagged.
   *  Standalone callers leave this null. */
  extraActiveKeys: PropTypes.instanceOf(Set),
  /** External save-in-flight indicator; disables buttons when true. */
  saving: PropTypes.bool,
  /** External error message from the caller (transcript mode). */
  error: PropTypes.string,
  /** "is-ai" | "is-manual" | "is-update" — extra class on root + Save button. */
  themeClass: PropTypes.string,
  /** When true, renders the "AI captured" / "Updating" chip above the label row. */
  /** Extra class on the Treatment-status Select's portaled menu — z-index scoping. */
  menuClassName: PropTypes.string,
  /**
   * Lock the diagnosis name + ICD picker as read-only. Automatically
   * implied by update mode; add-mode callers set true when reopening
   * an already-saved captured item (in-call re-edit, review click-to-
   * edit) so the reviewer can only tweak status / duration / description
   * / consulted from here.
   */
  lockName: PropTypes.bool,
  /** Transcript-mode: true when this card is a re-open of something already
   *  saved earlier in this call. Gates the top-right X close affordance so
   *  first-opens (#1/#3/#5/#7) show only the footer Reject. */
  isReopen: PropTypes.bool,
};

DiagnosisForm.defaultProps = {
  initialPayload: null,
  onSave: null,
  extraActiveKeys: null,
  saving: false,
  error: "",
  themeClass: "",
  menuClassName: "",
  lockName: false,
  isReopen: false,
};

export default DiagnosisForm;
